import { useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { Footer } from "../../components/dashboard/Footer";
import { ImpersonationBanner } from "../../components/dashboard/ImpersonationBanner";
import { PlatformUnavailable } from "../../components/dashboard/PlatformUnavailable";
import { AppSwitcher } from "../../components/dashboard/top-nav/AppSwitcher";
import { ProfileMenu } from "../../components/dashboard/top-nav/ProfileMenu";
import styles from "./DashboardShell.module.css";

import "@fontsource-variable/jetbrains-mono";
import "@fontsource-variable/bricolage-grotesque/wdth.css";

// Local dev runs ztc-core under the dev-auth Spring profile (Cognito isn't
// configured locally), so /oauth2/* 500s. The /dev-auth picker handles login
// in dev. Vite's import.meta.env.DEV is true only when serving via `bun run dev`.
const LOGIN_URL = import.meta.env.DEV
  ? "/dev-auth"
  : "/oauth2/authorization/cognito";

// Router Link, not <a href> — an anchor would full-reload the SPA (auth
// bootstrap, SSE reconnect, every fetch) on what should be an instant
// client-side route change.
const Wordmark = (): JSX.Element => (
  <Link className={styles.wordmark} to="/dashboard" aria-label="zerotoclaw">
    <span>zero</span>
    <span className={styles.wordmarkBracket}>[</span>
    <span className={styles.wordmarkRed}>to</span>
    <span className={styles.wordmarkBracket}>]</span>
    <span>claw</span>
  </Link>
);

export const DashboardShell = (): JSX.Element => {
  const { status, user, unavailableStatus } = useAuth();
  const isAdminRoute = useLocation().pathname.startsWith("/dashboard/admin");

  useEffect(() => {
    if (status === "unauthenticated") {
      window.location.replace(LOGIN_URL);
    }
  }, [status]);

  // ztc-core is down, not the session. Never fall through to the sign-in
  // redirect here: /oauth2/* is ALB-backed, so during an outage that
  // navigation is the raw 503 page.
  if (status === "unavailable") {
    return (
      <div className={styles.fontScope}>
        <PlatformUnavailable httpStatus={unavailableStatus} />
      </div>
    );
  }

  if (status !== "authenticated" || !user) {
    return (
      <div className={styles.fontScope} data-modal-host="">
        <div className={styles.gate}>
          <p className={styles.gateText}>
            {status === "loading" ? "Loading…" : "Redirecting to sign in…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.fontScope} data-modal-host="">
      {user.impersonation && <ImpersonationBanner state={user.impersonation} />}
      <header className={styles.topNav}>
        <div className={styles.topNavInner}>
          <div className={styles.navLeft}>
            {isAdminRoute && <AppSwitcher />}
            <Wordmark />
            {isAdminRoute && <span className={styles.appName}>Platform admin</span>}
          </div>
          <div className={styles.navRight}>
            <ProfileMenu user={user} />
          </div>
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
};
